import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { NativeSelect } from "@/components/ui/native-select";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { extractErrorMessage } from "@/lib/errorMessage";
import { ArrowUpCircle, Trash2, Plus, Paperclip, Scale, Truck } from "lucide-react";

const fmtBRL = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

// A.2 — Entrada de material (compra): NF obrigatória (ou Sem NF + justificativa),
// fornecedor obrigatório, várias linhas da mesma NF, conferência NF × pedido e aviso de preço >20%.
export function EntradaNFForm({ materiais, onSaved }) {
  const [fornecedores, setFornecedores] = useState([]);
  const [cab, setCab] = useState({ nota_fiscal: "", sem_nf: false, justificativa_sem_nf: "", fornecedor_id: "", data: new Date().toISOString().slice(0, 10), conferido_nf: false, anexo_base64: null });
  const [linhas, setLinhas] = useState([{ material_id: "", quantidade: 1, valor_unitario: 0 }]);
  const [saving, setSaving] = useState(false);
  const [avisos, setAvisos] = useState([]);

  useEffect(() => {
    axios.get(`${API}/clientes-fornecedores?tipo=fornecedor`).then(r => setFornecedores(r.data)).catch(() =>
      axios.get(`${API}/clientes-fornecedores`).then(r => setFornecedores(r.data)).catch(() => {}));
  }, []);

  const setLinha = (i, k, v) => setLinhas(ls => ls.map((x, j) => {
    if (j !== i) return x;
    const nova = { ...x, [k]: v };
    if (k === "material_id") nova.valor_unitario = (materiais.find(m => m.id === v) || {}).valor_unitario || 0;
    return nova;
  }));
  const total = linhas.reduce((s, l) => s + (Number(l.quantidade) || 0) * (Number(l.valor_unitario) || 0), 0);

  const anexar = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const rd = new FileReader();
    rd.onload = () => setCab(c => ({ ...c, anexo_base64: rd.result }));
    rd.readAsDataURL(f);
  };

  const submit = async () => {
    setSaving(true);
    try {
      const r = await axios.post(`${API}/estoque/entrada-nf`, {
        ...cab,
        linhas: linhas.filter(l => l.material_id).map(l => ({ material_id: l.material_id, quantidade: Number(l.quantidade), valor_unitario: Number(l.valor_unitario) })),
      });
      toast.success(r.data.message);
      setAvisos(r.data.avisos_preco || []);
      setCab({ nota_fiscal: "", sem_nf: false, justificativa_sem_nf: "", fornecedor_id: "", data: new Date().toISOString().slice(0, 10), conferido_nf: false, anexo_base64: null });
      setLinhas([{ material_id: "", quantidade: 1, valor_unitario: 0 }]);
      onSaved();
    } catch (err) { toast.error(extractErrorMessage(err, "Erro ao registrar entrada")); }
    finally { setSaving(false); }
  };

  const atualizarPreco = async (a) => {
    try {
      await axios.post(`${API}/estoque/${a.material_id}/atualizar-preco`, { valor_unitario: a.preco_nf });
      toast.success(`Preço de ${a.material_nome} atualizado para ${fmtBRL(a.preco_nf)}`);
      setAvisos(av => av.filter(x => x.material_id !== a.material_id));
      onSaved();
    } catch { toast.error("Erro ao atualizar preço"); }
  };

  const opcoesMat = materiais.map(m => ({ value: m.id, label: `${m.codigo} — ${m.nome}` }));
  return (
    <Card className="border-l-4 border-l-green-500" data-testid="form-entrada-nf">
      <CardContent className="p-4 space-y-3">
        <h3 className="font-semibold flex items-center gap-2 text-green-700">
          <ArrowUpCircle className="h-5 w-5" /> Entrada de material (compra) — conferência NF × pedido
        </h3>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">Nota Fiscal {cab.sem_nf ? "" : "*"}</Label>
            <Input value={cab.nota_fiscal} disabled={cab.sem_nf} onChange={(e) => setCab(c => ({ ...c, nota_fiscal: e.target.value }))} placeholder="Nº da NF" data-testid="input-nf" />
          </div>
          <div>
            <Label className="text-xs">Data</Label>
            <Input type="date" value={cab.data} onChange={(e) => setCab(c => ({ ...c, data: e.target.value }))} data-testid="input-data-nf" />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <Checkbox checked={cab.sem_nf} onCheckedChange={(v) => setCab(c => ({ ...c, sem_nf: !!v }))} data-testid="check-sem-nf" /> Sem NF (exige justificativa)
        </label>
        {cab.sem_nf && (
          <Input value={cab.justificativa_sem_nf} onChange={(e) => setCab(c => ({ ...c, justificativa_sem_nf: e.target.value }))}
            placeholder="Justificativa da entrada sem NF *" data-testid="input-justificativa-sem-nf" />
        )}
        <div>
          <Label className="text-xs">Fornecedor *</Label>
          <NativeSelect value={cab.fornecedor_id} onValueChange={(v) => setCab(c => ({ ...c, fornecedor_id: v }))} searchable
            placeholder="Selecione o fornecedor…" options={fornecedores.map(f => ({ value: f.id, label: f.nome }))} data-testid="select-fornecedor-nf" />
        </div>

        <div className="space-y-2">
          {linhas.map((l, i) => (
            <div key={i} className="flex items-end gap-2" data-testid={`linha-nf-${i}`}>
              <div className="flex-1">
                {i === 0 && <Label className="text-xs">Material</Label>}
                <NativeSelect value={l.material_id} onValueChange={(v) => setLinha(i, "material_id", v)} placeholder="Material…" options={opcoesMat} data-testid={`select-material-nf-${i}`} searchable />
              </div>
              <div className="w-20">
                {i === 0 && <Label className="text-xs">Qtd</Label>}
                <Input type="number" min="0.01" step="any" value={l.quantidade} onChange={(e) => setLinha(i, "quantidade", e.target.value)} data-testid={`input-qtd-nf-${i}`} />
              </div>
              <div className="w-28">
                {i === 0 && <Label className="text-xs">Valor unit. (NF)</Label>}
                <Input type="number" min="0" step="any" value={l.valor_unitario} onChange={(e) => setLinha(i, "valor_unitario", e.target.value)} data-testid={`input-valor-nf-${i}`} />
              </div>
              <Button variant="ghost" size="sm" onClick={() => setLinhas(ls => ls.length > 1 ? ls.filter((_, j) => j !== i) : ls)}>
                <Trash2 className="h-4 w-4 text-red-500" />
              </Button>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => setLinhas(ls => [...ls, { material_id: "", quantidade: 1, valor_unitario: 0 }])} data-testid="btn-add-linha-nf">
            <Plus className="h-4 w-4 mr-1" /> Outro material da mesma NF
          </Button>
        </div>

        <div className="flex items-center justify-between flex-wrap gap-2">
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer border border-slate-300 rounded-lg px-3 py-2">
            <Paperclip className="h-3.5 w-3.5" /> {cab.anexo_base64 ? "NF anexada ✓" : "Anexar foto/PDF da NF (opcional)"}
            <input type="file" accept="image/*,.pdf" className="hidden" onChange={anexar} data-testid="input-anexo-nf" />
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700 font-medium">
            <Checkbox checked={cab.conferido_nf} onCheckedChange={(v) => setCab(c => ({ ...c, conferido_nf: !!v }))} data-testid="check-conferido-nf" />
            Conferido NF × pedido
          </label>
          <span className="text-sm font-bold text-luvcorp-navy">Total: {fmtBRL(total)}</span>
        </div>

        <Button onClick={submit} disabled={saving || !cab.fornecedor_id || !linhas.some(l => l.material_id)} className="w-full bg-green-600 hover:bg-green-700 text-white" data-testid="btn-salvar-entrada-nf">
          {saving ? "Gravando…" : "Registrar entrada da NF"}
        </Button>

        {avisos.map(a => (
          <div key={a.material_id} className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-xs text-amber-800 flex items-center justify-between gap-2" data-testid={`aviso-preco-${a.material_id}`}>
            <span><b>{a.material_nome}</b>: preço da NF {fmtBRL(a.preco_nf)} difere {a.diferenca_pct}% do cadastro ({fmtBRL(a.preco_cadastro)}).</span>
            <Button size="sm" variant="outline" onClick={() => atualizarPreco(a)} data-testid={`btn-atualizar-preco-${a.material_id}`}>Atualizar cadastro</Button>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

// A.1 — Saída removida do Estoque (aviso) + Ajuste de inventário com justificativa
export function SaidaAjustePanel({ materiais, onSaved }) {
  const [aj, setAj] = useState({ material_id: "", quantidade_contada: "", justificativa: "" });
  const [saving, setSaving] = useState(false);
  const mat = materiais.find(m => m.id === aj.material_id);

  const ajustar = async () => {
    setSaving(true);
    try {
      const r = await axios.post(`${API}/estoque/ajuste-inventario`, {
        material_id: aj.material_id, quantidade_contada: Number(aj.quantidade_contada), justificativa: aj.justificativa });
      toast.success(r.data.message);
      setAj({ material_id: "", quantidade_contada: "", justificativa: "" });
      onSaved();
    } catch (err) { toast.error(extractErrorMessage(err, "Erro no ajuste")); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <Card className="border-l-4 border-l-luvcorp-gold" data-testid="aviso-saida-romaneio">
        <CardContent className="p-4 space-y-2">
          <h3 className="font-semibold flex items-center gap-2 text-luvcorp-navy"><Truck className="h-5 w-5 text-luvcorp-gold" /> Saída de material</h3>
          <p className="text-sm text-slate-600">A saída para obra (e o consumo interno da base/escritório) agora é feita <b>somente pelo Romaneio</b>, com conferência na entrega. Os lançamentos antigos continuam no histórico, marcados como "Lançamento antigo (sem romaneio)".</p>
          <Button onClick={() => (window.location.href = "/logistica/nova-saida")} className="bg-luvcorp-navy text-white" data-testid="btn-criar-romaneio-saida">
            Criar romaneio de saída
          </Button>
        </CardContent>
      </Card>
      <Card className="border-l-4 border-l-slate-400" data-testid="form-ajuste-inventario">
        <CardContent className="p-4 space-y-2">
          <h3 className="font-semibold flex items-center gap-2 text-slate-700"><Scale className="h-5 w-5" /> Ajuste de inventário (contagem)</h3>
          <NativeSelect value={aj.material_id} onValueChange={(v) => setAj(a => ({ ...a, material_id: v }))} searchable
            placeholder="Material…" options={materiais.map(m => ({ value: m.id, label: `${m.codigo} — ${m.nome} (saldo ${m.quantidade_atual})` }))} data-testid="select-material-ajuste" />
          <Input type="number" min="0" step="any" value={aj.quantidade_contada} onChange={(e) => setAj(a => ({ ...a, quantidade_contada: e.target.value }))}
            placeholder={mat ? `Quantidade contada (saldo atual: ${mat.quantidade_atual})` : "Quantidade contada"} data-testid="input-qtd-contada" />
          <Input value={aj.justificativa} onChange={(e) => setAj(a => ({ ...a, justificativa: e.target.value }))}
            placeholder="Justificativa (obrigatória)" data-testid="input-justificativa-ajuste" />
          <Button onClick={ajustar} disabled={saving || !aj.material_id || aj.quantidade_contada === "" || aj.justificativa.trim().length < 5}
            variant="outline" className="w-full" data-testid="btn-salvar-ajuste">Registrar ajuste de inventário</Button>
        </CardContent>
      </Card>
    </div>
  );
}
